"use client"

import {
  format,
  isAfter,
  isBefore,
  startOfMonth,
  endOfMonth,
  subMonths,
} from "date-fns"
import * as React from "react"
import { type DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"

import { DateInput } from "./date-input"

import Icons from "@/components/icons"
import { Button } from "@/components/ui/v2/button"
import { Calendar } from "@/components/ui/v2/calendar"
import { Label } from "@/components/ui/v2/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/v2/popover"
import { cn } from "@/lib/utils/cn"
import { getDateFnsLocale, parseLocalDateOnly } from "@/utils/dateUtils"

export interface DateRangePickerProps {
  onUpdate?: (values: { range: DateRange }) => void
  initialDateFrom?: Date | string
  initialDateTo?: Date | string
  align?: "start" | "center" | "end"
  minDate?: Date
  maxDate?: Date
  /** When set, only these preset IDs are shown. Use DateRangeQuickFilters. */
  allowedPresetIds?: readonly DateRangePresetId[]
  disabled?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Quick filter definitions (single source of truth)
// ---------------------------------------------------------------------------

export type DateRangeQuickFilterDays = {
  id: string
  type: "days"
  days: number
}
export type DateRangeQuickFilterMonth = {
  id: string
  type: "month"
  month: "this" | "last"
}
export type DateRangeQuickFilter =
  | DateRangeQuickFilterDays
  | DateRangeQuickFilterMonth

const QUICK_FILTER_DEFINITIONS: readonly DateRangeQuickFilter[] = [
  { id: "today", type: "days", days: 0 },
  { id: "yesterday", type: "days", days: 1 },
  { id: "last7days", type: "days", days: 7 },
  { id: "last14days", type: "days", days: 14 },
  { id: "last30days", type: "days", days: 30 },
  { id: "last3months", type: "days", days: 90 },
  { id: "last6months", type: "days", days: 180 },
  { id: "thisMonth", type: "month", month: "this" },
  { id: "lastMonth", type: "month", month: "last" },
] as const

export type DateRangePresetId =
  (typeof QUICK_FILTER_DEFINITIONS)[number]["id"]

export const DATE_RANGE_PRESET_IDS: readonly DateRangePresetId[] =
  QUICK_FILTER_DEFINITIONS.map((p) => p.id)

export const DateRangeQuickFilters = {
  all: DATE_RANGE_PRESET_IDS,
  allExceptToday: DATE_RANGE_PRESET_IDS.filter(
    (id) => id !== "today"
  ) as readonly DateRangePresetId[],
  except: (...exclude: DateRangePresetId[]) => {
    const set = new Set(exclude)
    return DATE_RANGE_PRESET_IDS.filter(
      (id) => !set.has(id)
    ) as readonly DateRangePresetId[]
  },
} as const

function parseDate(date: Date | string | undefined): Date | undefined {
  if (!date) return undefined
  if (date instanceof Date) return date
  return parseLocalDateOnly(String(date))
}

function formatDateForDisplay(
  date: Date | undefined,
  locale?: ReturnType<typeof getDateFnsLocale>
): string {
  if (!date) return ""
  return locale
    ? format(date, "PP", { locale })
    : format(date, "MMM d, yyyy")
}

/** Clamp date to [minDate, maxDate] when provided. */
function clamp(date: Date, min?: Date, max?: Date): Date {
  let d = new Date(date)
  if (min && isBefore(d, min)) d = new Date(min)
  if (max && isAfter(d, max)) d = new Date(max)
  return d
}

/** Ensure from <= to; swap if both set and from > to. */
function normalizeRange(range: DateRange): DateRange {
  const { from, to } = range
  if (from && to && isAfter(from, to)) {
    return { from: to, to: from }
  }
  return range
}

export function DateRangePicker({
  onUpdate,
  initialDateFrom,
  initialDateTo,
  align = "start",
  minDate,
  maxDate,
  allowedPresetIds,
  disabled = false,
  className,
}: DateRangePickerProps) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const startId = React.useId()
  const endId = React.useId()
  const liveRegionId = React.useId()

  const [range, setRange] = React.useState<DateRange>(() => ({
    from: parseDate(initialDateFrom),
    to: parseDate(initialDateTo),
  }))
  const [openedRange, setOpenedRange] = React.useState<DateRange>(() => ({
    from: parseDate(initialDateFrom),
    to: parseDate(initialDateTo),
  }))

  const [calendarMonth, setCalendarMonth] = React.useState<Date>(() => new Date())
  React.useEffect(() => {
    if (range.from == null) {
      setCalendarMonth(range.to ?? new Date())
    }
  }, [range.from, range.to])

  const filteredPresets = React.useMemo(() => {
    if (allowedPresetIds?.length) {
      const idSet = new Set(allowedPresetIds)
      return QUICK_FILTER_DEFINITIONS.filter((p) => idSet.has(p.id))
    }
    return [...QUICK_FILTER_DEFINITIONS]
  }, [allowedPresetIds])

  const { daysPresets, monthPresets } = React.useMemo(() => {
    const days: typeof filteredPresets = []
    const months: typeof filteredPresets = []
    for (const p of filteredPresets) {
      if (p.type === "days") days.push(p)
      else months.push(p)
    }
    return { daysPresets: days, monthPresets: months }
  }, [filteredPresets])

  const handlePresetSelect = React.useCallback(
    (preset: DateRangeQuickFilter) => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if (preset.type === "month") {
        if (preset.month === "this") {
          const from = startOfMonth(today)
          let to = new Date(today)
          to = clamp(to, minDate, maxDate)
          setRange({ from, to })
        } else {
          const lastMonth = subMonths(today, 1)
          setRange({
            from: startOfMonth(lastMonth),
            to: endOfMonth(lastMonth),
          })
        }
        return
      }

      const days = preset.days
      const endDate = clamp(new Date(today), minDate, maxDate)

      if (days === 0) {
        setRange({ from: endDate, to: endDate })
        return
      }
      if (days === 1) {
        if (maxDate && endDate.getTime() === maxDate.getTime()) {
          setRange({ from: endDate, to: endDate })
        } else {
          const from = new Date(endDate)
          from.setDate(from.getDate() - 1)
          setRange({ from, to: from })
        }
        return
      }

      const from = new Date(endDate)
      from.setDate(from.getDate() - days + 1)
      setRange({ from: clamp(from, minDate, undefined), to: endDate })
    },
    [minDate, maxDate]
  )

  const handleApply = () => {
    onUpdate?.({ range })
    setOpenedRange(range)
    setOpen(false)
  }

  const handleCancel = () => {
    setRange(openedRange)
    setOpen(false)
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setOpenedRange(range)
      setCalendarMonth(range.from ?? range.to ?? new Date())
    } else {
      setRange(openedRange)
    }
    setOpen(newOpen)
  }

  const dateLocale = React.useMemo(
    () => getDateFnsLocale(i18n.language),
    [i18n.language]
  )

  const displayValue = React.useMemo(() => {
    if (!range.from) return t("common.dateRangePicker.pickPlaceholder")
    const fromStr = formatDateForDisplay(range.from, dateLocale)
    const toStr = range.to ? formatDateForDisplay(range.to, dateLocale) : fromStr
    return fromStr === toStr ? fromStr : `${fromStr} - ${toStr}`
  }, [range, t, dateLocale])

  const isDateDisabled = React.useCallback(
    (date: Date) => {
      if (minDate && isBefore(date, minDate)) return true
      if (maxDate && isAfter(date, maxDate)) return true
      return false
    },
    [minDate, maxDate]
  )

  const liveRegionText = React.useMemo(() => {
    if (!range.from) return ""
    const fromStr = formatDateForDisplay(range.from, dateLocale)
    const toStr = range.to ? formatDateForDisplay(range.to, dateLocale) : fromStr
    return fromStr === toStr ? fromStr : `${fromStr} – ${toStr}`
  }, [range, dateLocale])

  return (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-auto justify-start text-left font-normal border-border",
            className
          )}
          disabled={disabled}
          aria-label={t("common.dateRangePicker.triggerLabel", {
            defaultValue: "Select date range",
          })}
        >
          <Icons.CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
          <span className={cn("text-sm", !range.from && "text-muted-foreground")}>{displayValue}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 border-border overflow-hidden flex flex-col"
        style={{
          maxWidth: "var(--radix-popover-content-available-width)",
          maxHeight: "var(--radix-popover-content-available-height)",
        }}
        side="bottom"
        align={align}
        sideOffset={8}
        collisionPadding={16}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div
          id={liveRegionId}
          className="sr-only"
          aria-live="polite"
          aria-atomic="true"
        >
          {liveRegionText}
        </div>
        <div className="flex flex-col md:flex-row overflow-hidden flex-1 min-h-0">
          <div className="flex flex-row md:flex-col overflow-x-auto md:overflow-visible flex-shrink-0 border-b md:border-b-0 md:border-r border-border p-2 gap-1 md:min-w-[7rem]">
            {daysPresets.map((preset) => (
              <Button
                key={preset.id}
                variant="ghost"
                size="sm"
                onClick={() => handlePresetSelect(preset)}
                className="justify-start text-sm flex-shrink-0 md:flex-shrink"
              >
                {t(`common.dateRangePicker.presets.${preset.id}`)}
              </Button>
            ))}
            {daysPresets.length > 0 && monthPresets.length > 0 && (
              <div
                className="flex-shrink-0 mx-1 my-0 md:mx-0 md:my-1 w-px md:w-full h-4 md:h-px border-l md:border-l-0 md:border-t border-border self-stretch md:self-auto"
                aria-hidden
              />
            )}
            {monthPresets.map((preset) => (
              <Button
                key={preset.id}
                variant="ghost"
                size="sm"
                onClick={() => handlePresetSelect(preset)}
                className="justify-start text-sm flex-shrink-0 md:flex-shrink"
              >
                {t(`common.dateRangePicker.presets.${preset.id}`)}
              </Button>
            ))}
          </div>

          <div className="flex flex-col min-w-0 overflow-hidden flex-1 min-h-0">
            <div className="flex flex-col overflow-auto min-h-0 flex-1">
              <div className="flex flex-col sm:flex-row gap-4 p-3 w-full">
                <div className="flex flex-1 flex-col gap-2 min-w-0">
                  <Label
                    htmlFor={startId}
                    className="text-xs text-muted-foreground"
                  >
                    {t("common.dateRangePicker.startDate")}
                  </Label>
                  <DateInput
                    id={startId}
                    value={range.from}
                    onChange={(date) => {
                      if (date === undefined) {
                        const fallback = new Date(range.to ?? Date.now())
                        fallback.setHours(0, 0, 0, 0)
                        const d = clamp(fallback, minDate, maxDate)
                        setRange({ from: d, to: range.to ?? d })
                      } else {
                        const clamped = clamp(date, minDate, maxDate)
                        setRange(normalizeRange({ ...range, from: clamped }))
                      }
                    }}
                    placeholder={t("common.dateRangePicker.startDatePlaceholder")}
                    className="w-full min-w-0"
                    locale={dateLocale}
                  />
                </div>
                <div className="hidden sm:flex items-end pb-2 shrink-0">
                  <Icons.ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex flex-1 flex-col gap-2 min-w-0">
                  <Label
                    htmlFor={endId}
                    className="text-xs text-muted-foreground"
                  >
                    {t("common.dateRangePicker.endDate")}
                  </Label>
                  <DateInput
                    id={endId}
                    value={range.to}
                    onChange={(date) => {
                      if (date === undefined) {
                        const fallback = new Date(range.from ?? Date.now())
                        fallback.setHours(0, 0, 0, 0)
                        const d = clamp(fallback, minDate, maxDate)
                        setRange({ from: range.from ?? d, to: d })
                      } else {
                        const clamped = clamp(date, minDate, maxDate)
                        setRange(normalizeRange({ ...range, to: clamped }))
                      }
                    }}
                    placeholder={t("common.dateRangePicker.endDatePlaceholder")}
                    className="w-full min-w-0"
                    locale={dateLocale}
                  />
                </div>
              </div>

              <div className="p-3 pt-0 w-full min-w-0">
                <Calendar
                  className="w-full md:w-fit"
                  locale={dateLocale}
                  mode="range"
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  selected={range}
                  onSelect={(newRange) =>
                    newRange && setRange(normalizeRange(newRange))
                  }
                  numberOfMonths={2}
                  disabled={isDateDisabled}
                  weekStartsOn={1}
                  showOutsideDays={false}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-border p-4 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="bg-transparent border-border"
                onClick={handleCancel}
              >
                {t("common.cancel")}
              </Button>
              <Button size="sm" onClick={handleApply}>
                {t("common.dateRangePicker.apply")}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { type DateRange }
